import { cn } from "@/common/helpers/utils";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import { EmptyValue } from "@/components/ui/empty-value";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ValuationDeltaIcon } from "@/components/vehicle-valuation/valuation-delta-icon";
import i18next from "i18next";
import { type ReactNode } from "react";

export enum ValuationNumberFormat {
  Currency = "currency",
  Percentage = "percentage",
}

interface ValuationValueCellProps {
  confirmed: number | null | undefined;
  generated: number | null | undefined;
  numberFormat: ValuationNumberFormat;
  numberDecimals: number;
  additionalValueTooltipContent?: ReactNode;
  confirmedValueTransform?: (value: number) => number;
}

export function ValuationValueCell({
  confirmed,
  generated,
  numberFormat,
  numberDecimals,
  additionalValueTooltipContent,
  confirmedValueTransform,
}: ValuationValueCellProps) {
  const hasConfirmed = confirmed != null;
  const displayedConfirmed = hasConfirmed ? (confirmedValueTransform?.(confirmed) ?? confirmed) : confirmed;
  const options = { numberFormat, numberDecimals };
  const valueTooltipLabel = hasConfirmed
    ? i18next.t("masterdata:ConfirmedValue")
    : i18next.t("masterdata:GeneratedValue");

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-1 text-sm">
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  "font-medium",
                  additionalValueTooltipContent
                    ? "cursor-help underline decoration-dotted underline-offset-2"
                    : "cursor-default",
                )}
              >
                {automaticCellRenderer(hasConfirmed ? displayedConfirmed : generated, options) ?? <EmptyValue />}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {additionalValueTooltipContent ? (
                <div className="max-w-72 space-y-2">
                  <p>{valueTooltipLabel}</p>
                  <div className="border-border/60 border-t pt-2">{additionalValueTooltipContent}</div>
                </div>
              ) : (
                valueTooltipLabel
              )}
            </TooltipContent>
          </Tooltip>
          {hasConfirmed && <ValuationDeltaIcon generated={generated} confirmed={displayedConfirmed} />}
        </div>
        {hasConfirmed && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-muted-foreground cursor-default text-xs">
                {automaticCellRenderer(generated, options)}
              </span>
            </TooltipTrigger>
            <TooltipContent>{i18next.t("masterdata:GeneratedValue")}</TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
